/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import { useIsDarkMode } from "../../theme/hooks";
import { getAdaptiveScaleMin, getObservedKpiMin } from "./kpiScale";

export interface KpiCardProps {
  title: string;
  value: number;
  unit?: string;
  scaleMin?: number;
  scaleMax?: number;
  target?: number;
  delta?: number;
  deltaLabel?: string;
  /** When true, reserve the sparkline column. When false (rolling modes), omit it entirely. */
  sparklineEnabled?: boolean;
  trendData?: { month: string; value: number }[];
  referenceValue?: number;
}


const KpiCard: React.FC<KpiCardProps> = ({
  title,
  value,
  unit = "%",
  scaleMin = 98.5,
  scaleMax = 100,
  target = 99.5,
  delta,
  deltaLabel = "3M delta",
  sparklineEnabled = false,
  trendData,
  referenceValue,
}) => {
  const observedMin = getObservedKpiMin(value, trendData?.map((point) => point.value));
  const effectiveScaleMin = getAdaptiveScaleMin(observedMin, scaleMin);
  const isDark = useIsDarkMode();
  const range = Math.max(0.0001, scaleMax - effectiveScaleMin);
  const valuePct = Math.min(100, Math.max(0, ((value - effectiveScaleMin) / range) * 100));
  const targetPct = Math.min(100, Math.max(0, ((target - effectiveScaleMin) / range) * 100));

  const barFillColor = value >= target
    ? (isDark ? "#2f5470" : "#c4d6dd")
    : (isDark ? "#a35423" : "#feaa71");
  const dotColor = value >= target ? "#00d492" : "#ff637e";

  const deltaColor = delta === undefined
    ? "text-muted-foreground"
    : delta >= 0
    ? "[color:#33b891]"
    : "[color:#ffd749]";

  const scaleLabelsRef = React.useRef<HTMLDivElement | null>(null);
  const minLabelRef = React.useRef<HTMLSpanElement | null>(null);
  const maxLabelRef = React.useRef<HTMLSpanElement | null>(null);
  const targetLabelRef = React.useRef<HTMLSpanElement | null>(null);
  const [targetLabelLeftPx, setTargetLabelLeftPx] = React.useState<number | null>(null);

  React.useLayoutEffect(() => {
    const row = scaleLabelsRef.current;
    const minLabel = minLabelRef.current;
    const maxLabel = maxLabelRef.current;
    const targetLabel = targetLabelRef.current;
    if (!row || !minLabel || !maxLabel || !targetLabel) {
      return;
    }

    const rowWidth = row.clientWidth;
    const targetHalfWidth = targetLabel.offsetWidth / 2;
    const collisionPadding = 4;
    const minCenter = minLabel.offsetWidth + collisionPadding + targetHalfWidth;
    const maxCenter = rowWidth - maxLabel.offsetWidth - collisionPadding - targetHalfWidth;
    const desiredCenter = (targetPct / 100) * rowWidth;
    const clampedCenter = minCenter <= maxCenter
      ? Math.min(Math.max(desiredCenter, minCenter), maxCenter)
      : rowWidth / 2;

    setTargetLabelLeftPx((prev) =>
      prev !== null && Math.abs(prev - clampedCenter) < 0.5 ? prev : clampedCenter
    );
  }, [targetPct, effectiveScaleMin, scaleMax, target, unit, sparklineEnabled]);

  return (
    <div className="bg-card text-card-foreground rounded-lg border border-border shadow-sm p-3 xl:p-4 flex flex-col gap-2 hover:shadow-md transition-shadow">
      {/* Top row: title + KPI value */}
      <div className="flex flex-col gap-1 min-w-0">
        <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground truncate">
          {title}
        </span>
        <div className="flex items-baseline gap-2">
          <span className="text-[28px] xl:text-[32px] font-bold leading-none text-foreground tabular-nums">
            {value.toFixed(2)}
            <span className="text-lg font-medium text-muted-foreground ml-0.5">{unit}</span>
          </span>
        </div>
      </div>

      {/* Bottom row: progress bar (60%) + sparkline (40%) side by side */}
      <div className="flex items-center gap-2">
        {/* Progress scale — full width when no sparkline, 60% when sparkline is shown */}
        <div className="flex flex-col gap-1" style={{ width: sparklineEnabled ? "60%" : "100%" }}>
          <div className="relative h-2 rounded-full bg-muted overflow-visible">
            <div
              className="absolute left-0 top-0 h-full rounded-full"
              style={{ width: `${valuePct}%`, backgroundColor: barFillColor }}
            />
            <div
              className="absolute top-[-4px] bottom-[-4px] w-[2px] bg-foreground rounded-full"
              style={{ left: `${targetPct}%`, transform: "translateX(-50%)" }}
            />
            <div
              className="absolute top-1/2 w-3 h-3 rounded-full border-2 border-card shadow-sm"
              style={{ left: `${valuePct}%`, transform: "translate(-50%, -50%)", backgroundColor: dotColor }}
            />
          </div>
          <div ref={scaleLabelsRef} className="relative flex justify-between text-[9px] text-muted-foreground mt-1 select-none">
            <span ref={minLabelRef}>{effectiveScaleMin}{unit}</span>
            <span
              ref={targetLabelRef}
              className="absolute whitespace-nowrap"
              style={{
                left: targetLabelLeftPx === null ? `${targetPct}%` : `${targetLabelLeftPx}px`,
                transform: "translateX(-50%)",
              }}
            >
              {target} target
            </span>
            <span ref={maxLabelRef}>{scaleMax}{unit}</span>
          </div>
        </div>

        {/* Sparkline column — only rendered for quarter modes */}
        {sparklineEnabled && (
        <div className="flex flex-col items-end gap-0.5 shrink-0" style={{ width: "40%" }}>
          {trendData ? (
            <>
          {/* Bottom white dotted line = end of chart portion */}
          <div style={{
            width: "100%",
            height: 64,
            borderBottom: "1px dashed rgba(148,163,184,0.35)",
          }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData} margin={{ top: 0, right: 4, bottom: 4, left: 4 }}>
                {/* Hidden Y-axis locked to [scaleMin, scaleMax] so the gap between
                    the value line and the 99.5 reference is proportionally visible */}
                <YAxis
                  hide
                  domain={[effectiveScaleMin, scaleMax]}
                />
                {/* Month labels at top, with white dotted separator line beneath them */}
                <XAxis
                  dataKey="month"
                  orientation="top"
                  tick={{ fontSize: 9, fill: "var(--muted-foreground)" }}
                  tickLine={false}
                  axisLine={{ stroke: "rgba(148,163,184,0.3)", strokeDasharray: "3 3", strokeWidth: 1 }}
                  interval={0}
                />
                <RechartsTooltip
                  contentStyle={{
                    fontSize: 10,
                    padding: "2px 6px",
                    borderRadius: 4,
                    border: "1px solid var(--border)",
                    background: "var(--card)",
                    color: "var(--foreground)",
                  }}
                  formatter={(v: any) => [`${Number(v).toFixed(2)}${unit}`, title]}
                />
                {/* Blue dotted threshold line at 99.5 (target) */}
                <ReferenceLine
                  y={target}
                  stroke="#60a5fa"
                  strokeDasharray="4 3"
                  strokeWidth={1}
                />
                <Line
                  type="monotone"
                  dataKey="value"
                  stroke="#7f3eed"
                  strokeWidth={1.5}
                  dot={{ r: 2.5, fill: "#7f3eed", strokeWidth: 0 }}
                  activeDot={{ r: 3.5 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {delta !== undefined && (
            <span className={`text-[10px] font-medium ${deltaColor} tabular-nums text-right`}>
              {deltaLabel}: {delta >= 0 ? "+" : ""}{delta.toFixed(2)}{unit}
            </span>
          )}
            </>
          ) : (
            <div className="flex items-center justify-center h-16 w-full text-[9px] text-muted-foreground/50 italic">
              No trend data
            </div>
          )}
        </div>
        )}
      </div>
    </div>
  );
};

export default KpiCard;
